import React, { Dispatch, SetStateAction, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link } from "react-router-dom";
import DeletePopUp from "../DeletePopUp";
import { INotice } from "../../exports/interfaces";
import { fetchNotices } from "../../api/NoticeApi";
import { useLoader } from "../../context/LoaderContext";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import { darkenColor } from "../../exports/functions";
import { useOption } from "../../context/OptionContext";

const getTypeClass = (type: string) => {
  switch (type) {
    case "General":
      return "pill-primary";
    case "Saving and Credits":
      return "pill-danger";
    case "Specific":
      return "bg-warning text-dark";
    case "Multipurpose":
      return "pill-info";
    case "Agricultural":
      return "pill-success";
    default:
      return "bg-secondary";
  }
};

const NoticeTable = ({
  notice,
  setNotice,
}: {
  notice: INotice[];
  setNotice: Dispatch<SetStateAction<INotice[]>>;
}) => {
  const { setLoading } = useLoader();
  const { statusOptions } = useOption();
  const { canEdit, canDelete } = usePermission();
  const isMobile = useMobile();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = notice.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(notice.length / itemsPerPage);

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Notice Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const handleClose = () => {
    setShowDelete(false);
  };
  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/notices/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchNotices()
          .then((noticeData) => {
            setNotice(noticeData);
            setShowDelete(false);
            setDeleteId(null);
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Notice deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            setTimeout(() => {
              setLoading(false);
              dynamicFailed(`${error.message}`);
            }, 1000);
          });
      }
    } catch (error: any) {
      setTimeout(() => {
        dynamicFailed(`${error.message}`);
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="dashboard-card">
        <div className="card-body">
          <h6 className="mb-3">NOTICE MANAGEMENT</h6>

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>S. no.</th>
                  <th>Notice Title</th>
                  <th>Notice Type</th>
                  <th>Email Address</th>
                  <th>Notice Status</th>
                  <th className="sticky-action">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index}>
                    <td className="text-danger">
                      {String(indexOfFirstItem + index + 1).padStart(6, "0")}
                    </td>

                    <td>{item.noticeTitle}</td>

                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2 ${getTypeClass(item.noticeType)}`}
                      >
                        {item.noticeType}
                      </span>
                    </td>

                    <td>{item.noticeDetails}</td>

                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2`}
                         style={statusStyle(item.status)}
                      >
                        {item.status}
                      </span>
                    </td>

                    <td className="sticky-action">
                      <div className="d-flex gap-3 align-items-center">
                        <Link to={`/`}>
                          <i
                            className="cursor-pointer"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="View"
                          >
                            <Eye />
                          </i>
                        </Link>
                        <Link
                          to={
                            canEdit("notices")
                              ? `/notices/edit/${item.id}`
                              : "#"
                          }
                          onClick={(e) => {
                            if (!canEdit("notices")) {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            pointerEvents: canEdit("notices") ? "auto" : "none",
                            opacity: canEdit("notices") ? 1 : 0.5,
                          }}
                        >
                          <i
                            data-toggle="tooltip"
                            data-placement="top"
                            title={
                              canEdit("notices") ? "Edit" : "No permission"
                            }
                          >
                            <Pencil size={20} />
                          </i>
                        </Link>
                        <i
                          className={`fa-solid fa-trash text-danger ${
                            canDelete("notices") ? "cursor-pointer" : "opacity-50"
                          }`}
                          data-toggle="tooltip"
                          data-placement="top"
                          title={
                            canDelete("notices") ? "Delete" : "No permission"
                          }
                          style={{
                            pointerEvents: canDelete("notices") ? "auto" : "none",
                          }}
                          onClick={() => {
                            if (!canDelete("notices")) return;
                            setDeleteId(item.id);
                            setShowDelete(true);
                          }}
                        ></i>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`d-flex justify-content-between align-items-center flex-wrap ${isMobile ? "gap-2" : ""}`}
        >
          <small>
            {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, notice.length)} of{" "}
            {notice.length} Notices
          </small>

          <div className="pagination">
            <button
              className="pagination-arrow"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              <i className="fa-solid fa-angle-left"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-page ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="pagination-arrow"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              <i className="fa-solid fa-angle-right"></i>
            </button>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default NoticeTable;
